import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-slate-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <h1 className="text-xl font-bold text-[#101E4D]">FLOW</h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <article className="bg-white rounded-xl shadow-sm p-8 space-y-6">
          <div className="border-b pb-6">
            <h1 className="text-3xl font-bold text-[#101E4D] mb-2">
              Politique de Confidentialité
            </h1>
            <p className="text-slate-500">
              Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">1. Introduction</h2>
            <p className="text-slate-600 leading-relaxed">
              WEDO COACHING & CONSEIL ("nous", "notre", "nos") s'engage à protéger la confidentialité de vos données personnelles. 
              Cette politique de confidentialité explique comment nous collectons, utilisons, stockons et protégeons 
              vos informations lorsque vous utilisez notre plateforme FLOW.
            </p>
            <p className="text-slate-600 leading-relaxed">
              En utilisant FLOW, vous acceptez les pratiques décrites dans cette politique. 
              Si vous n'êtes pas d'accord avec cette politique, veuillez ne pas utiliser nos services.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">2. Données collectées</h2>
            <p className="text-slate-600 leading-relaxed">Nous collectons les catégories de données suivantes :</p>
            <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
              <li><strong>Données d'identification :</strong> nom, prénom, adresse email</li>
              <li><strong>Données de profil :</strong> profil de personnalité, photo de profil (optionnel)</li>
              <li><strong>Données d'utilisation :</strong> progression dans les modules, réponses aux exercices, dates d'accès</li>
              <li><strong>Données de paiement :</strong> historique des paiements (les données bancaires sont traitées par notre prestataire de paiement sécurisé)</li>
              <li><strong>Données techniques :</strong> adresse IP, type de navigateur, pour des raisons de sécurité</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">3. Finalités du traitement</h2>
            <p className="text-slate-600 leading-relaxed">Vos données sont utilisées pour :</p>
            <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
              <li>Fournir et personnaliser le parcours de coaching FLOW</li>
              <li>Gérer votre compte et authentifier votre identité</li>
              <li>Suivre votre progression et vous envoyer des rappels</li>
              <li>Traiter vos paiements et gérer votre abonnement</li>
              <li>Améliorer nos services et développer de nouvelles fonctionnalités</li>
              <li>Respecter nos obligations légales</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">4. Base légale du traitement</h2>
            <p className="text-slate-600 leading-relaxed">
              Conformément au RGPD, nous traitons vos données sur les bases légales suivantes :
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
              <li><strong>Exécution du contrat :</strong> pour fournir le service FLOW auquel vous avez souscrit</li>
              <li><strong>Consentement :</strong> pour l'envoi de communications marketing (vous pouvez retirer ce consentement à tout moment)</li>
              <li><strong>Intérêt légitime :</strong> pour améliorer nos services et assurer la sécurité de la plateforme</li>
              <li><strong>Obligation légale :</strong> pour respecter nos obligations comptables et fiscales</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">5. Conservation des données</h2>
            <p className="text-slate-600 leading-relaxed">
              Vos données sont conservées pendant la durée de votre inscription à FLOW, puis :
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
              <li>Données de compte : 3 ans après la dernière activité</li>
              <li>Données de paiement : 10 ans (obligation légale)</li>
              <li>Données de progression et réponses : supprimées sur demande ou 3 ans après la fin du parcours</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">6. Vos droits (RGPD)</h2>
            <p className="text-slate-600 leading-relaxed">
              Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants :
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
              <li><strong>Droit d'accès :</strong> obtenir une copie de vos données personnelles</li>
              <li><strong>Droit de rectification :</strong> corriger des données inexactes</li>
              <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données</li>
              <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré</li>
              <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
              <li><strong>Droit à la limitation :</strong> limiter le traitement de vos données</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              Pour exercer ces droits, vous pouvez utiliser les fonctionnalités disponibles dans votre espace personnel 
              (export de données, suppression de compte) ou nous contacter directement.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">7. Sécurité des données</h2>
            <p className="text-slate-600 leading-relaxed">
              Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos données :
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
              <li>Chiffrement des données en transit (HTTPS/TLS)</li>
              <li>Mots de passe hashés avec algorithme bcrypt</li>
              <li>Accès restreint aux données personnelles</li>
              <li>Surveillance et journalisation des accès</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">8. Partage des données</h2>
            <p className="text-slate-600 leading-relaxed">
              Nous ne vendons jamais vos données personnelles. Vos données peuvent être partagées avec :
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
              <li>Nos sous-traitants techniques (hébergement, envoi d'emails) dans le cadre strict de la fourniture du service</li>
              <li>Les autorités compétentes si la loi l'exige</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">9. Cookies</h2>
            <p className="text-slate-600 leading-relaxed">
              Notre plateforme utilise des cookies essentiels au fonctionnement du service (authentification, session). 
              Nous n'utilisons pas de cookies publicitaires ou de tracking tiers.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">10. Contact</h2>
            <p className="text-slate-600 leading-relaxed">
              Pour toute question concernant cette politique ou pour exercer vos droits, contactez-nous :
            </p>
            <div className="bg-slate-50 p-4 rounded-lg text-slate-600">
              <p><strong>WEDO COACHING & CONSEIL</strong></p>
              <p>35 Cours Pierre Puget, 13006 Marseille</p>
              <p>Email : contact@wedocoaching.com</p>
            </div>
            <p className="text-slate-600 leading-relaxed mt-4">
              Vous avez également le droit d'introduire une réclamation auprès de la CNIL 
              (Commission Nationale de l'Informatique et des Libertés) si vous estimez que 
              le traitement de vos données ne respecte pas la réglementation.
            </p>
          </section>
        </article>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
